// resources/js/components/bid_limits/api.ts
//
// Thin client for the /api/v1/bid-limits endpoints.

import { axiosDestroy, axiosPatch, axiosPost } from '../../util/api'
import { ResolvedBidLimits } from '../../types/bid_limits'

// The batch-resolve endpoint caps ids per request (see BatchResolveBidLimitsRequest).
const BATCH_LIMIT = 500

/**
 * Resolve effective bid limits for many entities of one type. Ids are sent as
 * strings to stay bigint-safe; the result is keyed by id (string).
 */
export const batchResolveBidLimits = async (
    parentType: number,
    parentIds: string[],
): Promise<Record<string, ResolvedBidLimits>> => {
    const out: Record<string, ResolvedBidLimits> = {}

    for (let i = 0; i < parentIds.length; i += BATCH_LIMIT) {
        const chunk = parentIds.slice(i, i + BATCH_LIMIT)
        const res = await axiosPost('bid-limits/batch-resolve', {
            parent_type: parentType,
            parent_ids: chunk,
        })
        Object.assign(out, res.data?.limits ?? {})
    }

    return out
}

/**
 * Set an entity's bid limits. A null min/max is an explicit "clear this side";
 * passing both null is rejected by the server — use clearBidLimits instead.
 * Returns the freshly resolved limits.
 */
export const saveBidLimits = async (
    parentType: number,
    parentId: string,
    minBid: number | null,
    maxBid: number | null,
): Promise<ResolvedBidLimits> => {
    const res = await axiosPatch('bid-limits', {
        parent_type: parentType,
        parent_id: parentId,
        min_bid: minBid,
        max_bid: maxBid,
    })

    return res.data
}

/** Reset an entity's bid limits to fully inherited. Returns the resolved limits. */
export const clearBidLimits = async (
    parentType: number,
    parentId: string,
): Promise<ResolvedBidLimits> => {
    const res = await axiosDestroy('bid-limits', {
        parent_type: parentType,
        parent_id: parentId,
    })

    return res.data
}
